import { Accordion, Box, Flex, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiOutlineCheckCircle, HiOutlineXCircle } from "react-icons/hi2";

const StatusIcon = ({ result }) => {
    if (!result) {
        return <Spinner size="sm" color="white" />;
    }
    if (result.passed) {
        return <Icon fontSize={22} color="green.400"><HiOutlineCheckCircle /></Icon>;
    }
    return <Icon fontSize={22} color="red.400"><HiOutlineXCircle /></Icon>;
};

const StatusLabel = ({ result }) => {
    if (!result) return <Text color="text.grey" fontSize={13}>Running...</Text>;
    if (result.passed) return <Text color="green.400" fontSize={13} fontWeight="semibold">Passed</Text>;
    return <Text color="red.400" fontSize={13} fontWeight="semibold">Failed</Text>;
};

const IncidentResultItem = ({ incident, result }) => {
    const hasFailed = result && !result.passed && result.failures?.length > 0;

    return (
        <Accordion.Root collapsible variant="none" w="full">
            <Accordion.Item value="incident" bg="bg.secondary" borderRadius={12} borderWidth="1px" borderColor="border.primary">
                <Accordion.ItemTrigger px={5} py={4} _hover={{ bg: "transparent" }}>
                    <Flex justifyContent="space-between" alignItems="center" w="full">
                        <VStack alignItems="flex-start" gap={1}>
                            <Text color="white" fontSize={15} fontWeight="semibold">{incident.name}</Text>
                            <Text color="text.grey" fontSize={12}>Scenario: {incident.scenarioId}</Text>
                        </VStack>
                        <HStack gap={3}>
                            <StatusLabel result={result} />
                            <StatusIcon result={result} />
                        </HStack>
                    </Flex>
                    <Accordion.ItemIndicator />
                </Accordion.ItemTrigger>
                <Accordion.ItemContent>
                    <Accordion.ItemBody px={5} pb={5} pt={0}>
                        <Accordion.Root multiple collapsible variant="none" w="full">
                            {hasFailed && (
                                <Accordion.Item value="failures" borderTopWidth="1px" borderColor="border.primary">
                                    <Accordion.ItemTrigger py={3} _hover={{ bg: "transparent" }}>
                                        <HStack gap={2}>
                                            <Text color="red.300" fontSize={13} fontWeight="semibold">Failures</Text>
                                            <Text color="red.500" fontSize={12}>({result.failures.length})</Text>
                                        </HStack>
                                        <Accordion.ItemIndicator />
                                    </Accordion.ItemTrigger>
                                    <Accordion.ItemContent>
                                        <Accordion.ItemBody pb={3}>
                                            <VStack gap={2} alignItems="flex-start" w="full">
                                                {result.failures.map((failure, i) => (
                                                    <Box key={i} w="full" bg="bg.dark" borderRadius={8} p={3} borderWidth="1px" borderColor="red.800">
                                                        <Text color="text.grey" fontSize={11} mb={1}>Message #{failure.messageIndex + 1} — input: <Text as="span" color="white">{failure.input ?? "null"}</Text></Text>
                                                        <Box mb={1}>
                                                            <Text color="text.grey" fontSize={11}>Actual response:</Text>
                                                            <Text color="red.300" fontSize={12}>{failure.actualResponse}</Text>
                                                        </Box>
                                                        {failure.expectedExact && (
                                                            <Box mb={1}>
                                                                <Text color="text.grey" fontSize={11}>Expected exact:</Text>
                                                                <Text color="green.300" fontSize={12}>{failure.expectedExact}</Text>
                                                            </Box>
                                                        )}
                                                        {failure.expectedPossible?.length > 0 && (
                                                            <Box>
                                                                <Text color="text.grey" fontSize={11}>Expected one of:</Text>
                                                                {failure.expectedPossible.map((p, j) => (
                                                                    <Text key={j} color="green.300" fontSize={12}>• {p}</Text>
                                                                ))}
                                                            </Box>
                                                        )}
                                                        {failure.expectedAnyOfPhrases?.length > 0 && (
                                                            <Box>
                                                                <Text color="text.grey" fontSize={11}>Expected any of phrases:</Text>
                                                                {failure.expectedAnyOfPhrases.map((p, j) => (
                                                                    <Text key={j} color="green.300" fontSize={12}>• {p}</Text>
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ))}
                                            </VStack>
                                        </Accordion.ItemBody>
                                    </Accordion.ItemContent>
                                </Accordion.Item>
                            )}
                            {result?.messages?.length > 0 && (
                                <Accordion.Item value="conversation" borderTopWidth="1px" borderColor="border.primary">
                                    <Accordion.ItemTrigger py={3} _hover={{ bg: "transparent" }}>
                                        <Text color="text.grey" fontSize={13} fontWeight="semibold">Conversation</Text>
                                        <Accordion.ItemIndicator />
                                    </Accordion.ItemTrigger>
                                    <Accordion.ItemContent>
                                        <Accordion.ItemBody pb={3}>
                                            <VStack gap={1} alignItems="stretch" w="full">
                                                {result.messages.map((msg, i) => (
                                                    <Flex key={i} justify={msg.origin === "User" ? "flex-end" : "flex-start"}>
                                                        <Box
                                                            maxW="80%"
                                                            bg="bg.dark"
                                                            borderRadius={8}
                                                            px={3}
                                                            py={2}
                                                            borderWidth="1px"
                                                            borderColor={msg.isFailure ? "red.800" : "border.primary"}
                                                        >
                                                            <Text fontSize={10} color="text.grey" mb={0.5}>{msg.origin}</Text>
                                                            <Text fontSize={12} color={msg.isFailure ? "red.300" : (msg.origin === "User" ? "blue.300" : "white")}>
                                                                {msg.message}
                                                            </Text>
                                                        </Box>
                                                    </Flex>
                                                ))}
                                            </VStack>
                                        </Accordion.ItemBody>
                                    </Accordion.ItemContent>
                                </Accordion.Item>
                            )}
                        </Accordion.Root>
                    </Accordion.ItemBody>
                </Accordion.ItemContent>
            </Accordion.Item>
        </Accordion.Root>
    );
};

export default IncidentResultItem;
